import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StudentNavigation from '../../components/ui/StudentNavigation';
import Icon from '../../components/AppIcon';
import { webinarService } from '../../services/webinarService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import WebinarCard from './components/WebinarCard';
import ExpertSpotlight from './components/ExpertSpotlight';
import FilterBar from './components/FilterBar';
import RegistrationModal from './components/RegistrationModal';
import JitsiMeetRoom from '../../components/JitsiMeetRoom';
import VideoPlayerModal from './components/VideoPlayerModal';

const AuditoriumWebinarHub = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const toast = useToast();
  const [webinars, setWebinars] = useState([]);
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLanguage, setSelectedLanguage] = useState('all');
  const [selectedExpert, setSelectedExpert] = useState('all');
  const [userRegistrations, setUserRegistrations] = useState([]);
  const [registrationModal, setRegistrationModal] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [activeWebinar, setActiveWebinar] = useState(null); // Jitsi live session
  const [recordingWebinar, setRecordingWebinar] = useState(null); // Recording playback modal

  useEffect(() => {
    loadData();
  }, [selectedStatus, selectedCategory, selectedExpert]);

  const loadData = async () => {
    try {
      setLoading(true);

      const filters = {};
      if (selectedStatus !== 'all') filters.status = selectedStatus;
      if (selectedCategory !== 'all') filters.topic = selectedCategory;
      if (selectedExpert !== 'all') filters.expertId = selectedExpert;

      const [webinarsData, expertsData] = await Promise.all([
        webinarService?.getWebinars(filters),
        webinarService?.getExperts()
      ]);

      setWebinars(webinarsData || []);
      setExperts(expertsData || []);

      if (user) {
        const registrations = await webinarService?.getUserRegistrations(user?.id);
        setUserRegistrations(registrations || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast?.error('Failed to load webinars');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRegistrationModal = (webinar) => {
    if (!user) {
      toast?.error('Please sign in to register for webinars');
      navigate('/student-registration');
      return;
    }
    setRegistrationModal(webinar);
  };

  const handleRegister = async (webinarId) => {
    if (!user) {
      toast?.error('Please sign in to register for webinars');
      navigate('/student-registration');
      return;
    }

    try {
      setRegistering(true);
      await webinarService?.registerForWebinar(webinarId, user?.id);
      toast?.success('Successfully registered! You will receive a reminder before the session.');
      setRegistrationModal(null);
      await loadData();
    } catch (error) {
      console.error('Registration error:', error);
      toast?.error('Failed to register for webinar. Please try again.');
    } finally {
      setRegistering(false);
    }
  };

  const handleCancelRegistration = async (webinarId) => {
    if (!user) return;

    try {
      await webinarService?.cancelRegistration(webinarId, user?.id);
      toast?.success('Registration cancelled successfully');
      await loadData();
    } catch (error) {
      console.error('Cancellation error:', error);
      toast?.error('Failed to cancel registration');
    }
  };

  const handleJoinLive = (webinar) => {
    if (!user) {
      toast?.error('Please sign in to join this webinar');
      navigate('/student-registration');
      return;
    }
    // Use meeting_url if it's already a Jitsi URL, otherwise generate a room name from webinar id/title
    const roomName = webinar?.meeting_url?.includes('meet.jit.si')
      ? webinar?.meeting_url?.split('meet.jit.si/')?.[1]?.split('#')?.[0]
      : `StudyExpo-Webinar-${webinar?.id || Date.now()}`;
    setActiveWebinar({ ...webinar, jitsiRoom: roomName });
  };

  const handleLeaveWebinar = () => {
    setActiveWebinar(null);
  };

  const handleWatchRecording = (webinar) => {
    setRecordingWebinar(webinar);
  };

  const handleCloseRecording = () => {
    setRecordingWebinar(null);
  };

  const isRegistered = (webinarId) => {
    return userRegistrations?.some(
      (reg) => reg?.webinar?.id === webinarId && reg?.status === 'registered'
    );
  };

  const filteredWebinars = webinars?.filter((webinar) => {
    const matchesSearch = !searchQuery || (() => {
      const query = searchQuery?.toLowerCase();
      return (
        webinar?.title?.toLowerCase()?.includes(query) ||
        webinar?.description?.toLowerCase()?.includes(query) ||
        webinar?.expert?.name?.toLowerCase()?.includes(query) ||
        webinar?.topic?.toLowerCase()?.includes(query)
      );
    })();

    const matchesLanguage = selectedLanguage === 'all' || webinar?.language === selectedLanguage;

    return matchesSearch && matchesLanguage;
  });

  const upcomingWebinars = filteredWebinars?.filter((w) => w?.status === 'upcoming');
  const liveWebinars = filteredWebinars?.filter((w) => w?.status === 'live');
  const recordedWebinars = filteredWebinars?.filter((w) => w?.status === 'completed');
  const featuredExperts = experts?.slice(0, 3);

  const totalRegistrations = userRegistrations?.filter((r) => r?.status === 'registered')?.length;

  return (
    <div className="min-h-screen bg-background">
      <StudentNavigation />

      {/* Jitsi Embedded Webinar Overlay */}
      {activeWebinar && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col">
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-900 border-b border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-2.5 h-2.5 bg-error rounded-full animate-pulse" />
              <span className="text-white font-semibold text-sm">
                {activeWebinar?.title}
              </span>
            </div>
            <button
              onClick={handleLeaveWebinar}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-error hover:bg-error/80 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <Icon name="X" size={13} />
              Exit Webinar
            </button>
          </div>
          {/* Jitsi iframe */}
          <div className="flex-1 min-h-0">
            <JitsiMeetRoom
              roomName={activeWebinar?.jitsiRoom}
              displayName={userProfile?.name || user?.email || 'Student'}
              title={activeWebinar?.title}
              onClose={handleLeaveWebinar}
            />
          </div>
        </div>
      )}

      {/* Recording Playback Modal */}
      <VideoPlayerModal
        isOpen={!!recordingWebinar}
        onClose={handleCloseRecording}
        webinar={recordingWebinar}
      />

      <div className="pt-[60px]">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">

          {/* Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Icon name="Video" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                    Auditorium & Webinar Hub
                  </h1>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Live expert talks, educational webinars, and recorded sessions
                  </p>
                </div>
              </div>

              {/* Stats bar */}
              <div className="flex items-center gap-4 md:gap-6">
                <div className="text-center">
                  <p className="text-xl font-bold text-foreground">{webinars?.length}</p>
                  <p className="text-xs text-muted-foreground">Total Sessions</p>
                </div>
                <div className="w-px h-8 bg-border" />
                <div className="text-center">
                  <p className="text-xl font-bold text-error">{liveWebinars?.length}</p>
                  <p className="text-xs text-muted-foreground">Live Now</p>
                </div>
                <div className="w-px h-8 bg-border" />
                <div className="text-center">
                  <p className="text-xl font-bold text-primary">{totalRegistrations}</p>
                  <p className="text-xs text-muted-foreground">My Registrations</p>
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <FilterBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedStatus={selectedStatus}
            onStatusChange={setSelectedStatus}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            selectedLanguage={selectedLanguage}
            onLanguageChange={setSelectedLanguage}
            selectedExpert={selectedExpert}
            onExpertChange={setSelectedExpert}
            experts={experts}
          />

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">Loading webinars...</p>
              </div>
            </div>
          )}

          {!loading && (
            <>
              {/* Live Webinars - Priority section */}
              {liveWebinars?.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 bg-error rounded-full animate-pulse" />
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                      Live Now
                    </h2>
                    <span className="px-2.5 py-0.5 bg-error/10 text-error text-xs font-semibold rounded-full">
                      {liveWebinars?.length} session{liveWebinars?.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {liveWebinars?.map((webinar) => (
                      <WebinarCard
                        key={webinar?.id}
                        webinar={webinar}
                        isRegistered={isRegistered(webinar?.id)}
                        onRegister={handleRegister}
                        onCancelRegistration={handleCancelRegistration}
                        onOpenModal={handleOpenRegistrationModal}
                        onJoinLive={handleJoinLive}
                        onWatchRecording={handleWatchRecording}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Expert Spotlight */}
              {featuredExperts?.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="Star" size={20} color="var(--color-accent)" />
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                      Expert Spotlight
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {featuredExperts?.map((expert) => (
                      <ExpertSpotlight key={expert?.id} expert={expert} />
                    ))}
                  </div>
                </div>
              )}

              {/* Upcoming Webinars */}
              {upcomingWebinars?.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="Calendar" size={20} color="var(--color-primary)" />
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                      Upcoming Webinars
                    </h2>
                    <span className="px-2.5 py-0.5 bg-primary/10 text-primary text-xs font-semibold rounded-full">
                      {upcomingWebinars?.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {upcomingWebinars?.map((webinar) => (
                      <WebinarCard
                        key={webinar?.id}
                        webinar={webinar}
                        isRegistered={isRegistered(webinar?.id)}
                        onRegister={handleRegister}
                        onCancelRegistration={handleCancelRegistration}
                        onOpenModal={handleOpenRegistrationModal}
                        onJoinLive={handleJoinLive}
                        onWatchRecording={handleWatchRecording}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Recorded Sessions */}
              {recordedWebinars?.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="PlayCircle" size={20} color="var(--color-success)" />
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                      Recorded Sessions
                    </h2>
                    <span className="px-2.5 py-0.5 bg-success/10 text-success text-xs font-semibold rounded-full">
                      {recordedWebinars?.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {recordedWebinars?.map((webinar) => (
                      <WebinarCard
                        key={webinar?.id}
                        webinar={webinar}
                        isRegistered={false}
                        onRegister={handleRegister}
                        onCancelRegistration={handleCancelRegistration}
                        onOpenModal={handleOpenRegistrationModal}
                        onJoinLive={handleJoinLive}
                        onWatchRecording={handleWatchRecording}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {filteredWebinars?.length === 0 && (
                <div className="bg-card border border-border rounded-xl p-8 md:p-16 text-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Video" size={32} color="var(--color-muted-foreground)" />
                  </div>
                  <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2">
                    No Webinars Found
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground max-w-md mx-auto mb-6">
                    {searchQuery
                      ? 'Try adjusting your search or filters to find webinars' :'Check back soon for upcoming expert talks and webinars'}
                  </p>
                  {(searchQuery || selectedStatus !== 'all' || selectedCategory !== 'all' || selectedLanguage !== 'all') && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedStatus('all');
                        setSelectedCategory('all');
                        setSelectedLanguage('all');
                        setSelectedExpert('all');
                      }}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                    >
                      Clear All Filters
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Registration Modal */}
      {registrationModal && (
        <RegistrationModal
          webinar={registrationModal}
          onClose={() => setRegistrationModal(null)}
          onConfirm={handleRegister}
          isLoading={registering}
        />
      )}
    </div>
  );
};

export default AuditoriumWebinarHub;